import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, MenuItem, Select, FormControl, InputLabel,
  Paper, Button, Stack, Chip, Divider, Alert, CircularProgress
} from "@mui/material";
import axios from "axios";
import TableSnapshotAffissioniAdmin from "./TableSnapshotAffissioniAdmin";
import TableSnapshotDepositiAdmin from "./TableSnapshotDepositiAdmin";

const { REACT_APP_API_ENDPOINT = "" } = process.env;

export default function SnapshotDashboardAdmin() {
  const [tipo, setTipo] = useState(""); // "affissioni" | "depositi"
  const [campagne, setCampagne] = useState([]);
  const [campagnaSelezionata, setCampagnaSelezionata] = useState("");
  const [snapshot, setSnapshot] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [errorList, setErrorList] = useState(null);
  const [errorSnap, setErrorSnap] = useState(null);

  // lista campagne chiuse per tipo
  useEffect(() => {
    setCampagne([]);
    setCampagnaSelezionata("");
    setSnapshot(null);
    setErrorList(null);
    if (!tipo) return;

    setLoadingList(true);
    axios
      .get(`${REACT_APP_API_ENDPOINT}/api/snapshot/campagne-chiuse?tipo=${encodeURIComponent(tipo)}`)
      .then((res) => setCampagne(res.data || []))
      .catch((e) => setErrorList(e?.response?.data?.message || e?.message || "Errore nel caricamento campagne"))
      .finally(() => setLoadingList(false));
  }, [tipo]);

  // click → carica snapshot
  const handleLoadSnapshot = () => {
    if (!campagnaSelezionata) return;
    setErrorSnap(null);
    setSnapshot(null);
    setLoadingSnap(true);
    axios
      .get(`${REACT_APP_API_ENDPOINT}/api/snapshot/${campagnaSelezionata}`)
      .then((res) => setSnapshot(res.data))
      .catch((e) => setErrorSnap(e?.response?.data?.message || e?.message || "Errore nel caricamento snapshot"))
      .finally(() => setLoadingSnap(false));
  };

  const isAffissioni = tipo === "affissioni";
  const isDepositi = tipo === "depositi";

  // branch: uffici -> snapshot.admin ; depositi -> snapshot flat
  const branch = useMemo(() => {
    if (!snapshot) return null;
    return isAffissioni ? (snapshot?.snapshot?.admin || null) : (snapshot?.snapshot || null);
  }, [snapshot, isAffissioni]);

  const campaignName = snapshot?.campaignName || snapshot?.denominazione || "Campagna";
  const pageTitle = isDepositi ? "Snapshot Depositi — Admin" : "Snapshot Affissioni — Admin";

  // CHIP Uffici (come prima)
  const totalsAff = useMemo(() => {
    if (!branch || !isAffissioni) return null;
    return {
      totalOffices: typeof branch.totalOffices === "number" ? branch.totalOffices : null,
      managed: typeof branch.managed === "number" ? branch.managed : null,
      completed: typeof branch.completed === "number" ? branch.completed : null,
      inProgress: typeof branch.inProgress === "number" ? branch.inProgress : null,
      notStarted: typeof branch.notStarted === "number" ? branch.notStarted : null,
    };
  }, [branch, isAffissioni]);

  // CHIP Depositi — dedup per garageId + priorità stato (completed > in progress > not started)
  const totalsDepositi = useMemo(() => {
    if (!isDepositi) return null;
    const arr = snapshot?.snapshot?.dettaglioPerLuogo;
    if (!Array.isArray(arr)) return null;

    const getId = (r) => r?.garageId ?? r?.depositoId ?? r?.id ?? r?._id ?? null;
    const scoreOf = (r) => {
      // usa numeri se presenti, altrimenti 'stato'
      if (typeof r?.completati === 'number' && r.completati > 0) return 2;
      if (typeof r?.inProgress === 'number' && r.inProgress > 0) return 1;
      if (typeof r?.nonAvviati === 'number' && r.nonAvviati > 0) return 0;
      if (r?.stato === 'completed') return 2;
      if (r?.stato === 'in progress') return 1;
      if (r?.stato === 'not started') return 0;
      return 0;
    };

    const byId = new Map();
    for (const r of arr) {
      const id = getId(r);
      if (!id) continue;
      const sc = scoreOf(r);
      const prev = byId.get(id);
      if (prev == null || sc > prev) byId.set(id, sc);
    }

    let completed = 0, inProgress = 0, notStarted = 0;
    for (const sc of byId.values()) {
      if (sc === 2) completed++;
      else if (sc === 1) inProgress++;
      else notStarted++;
    }

    return { count: byId.size, completed, inProgress, notStarted };
  }, [isDepositi, snapshot]);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>{pageTitle}</Typography>

      {/* FILTRI */}
      <Paper sx={{ p: 2, mb: 2, maxWidth: 960, mx: 'auto' }} variant="outlined">
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "280px 280px auto" }} gap={2} justifyContent="center">
          <FormControl size="small" sx={{ width: 280 }}>
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={tipo || ""} onChange={(e) => setTipo(e.target.value)}>
              <MenuItem value="affissioni">Uffici</MenuItem>
              <MenuItem value="depositi">Depositi</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: 280 }} disabled={!campagne.length}>
            <InputLabel>Campagna</InputLabel>
            <Select
              label="Campagna"
              value={campagnaSelezionata || ""}
              onChange={(e) => setCampagnaSelezionata(e.target.value)}
            >
              {campagne.map((c) => (
                <MenuItem key={c.campaignId} value={c.campaignId}>
                  {c.denominazione}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" onClick={handleLoadSnapshot} disabled={!campagnaSelezionata || loadingSnap}>
            {loadingSnap ? "Caricamento…" : "Carica snapshot"}
          </Button>
        </Box>

        {loadingList && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Carico campagne…</Typography>
          </Box>
        )}

        {errorList && <Alert severity="error" sx={{ mt: 2 }}>{String(errorList)}</Alert>}
      </Paper>

      {/* CONTENUTO */}
      {snapshot && (
        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" spacing={2}>
            <Typography variant="h6">{campaignName}</Typography>
            {snapshot?.dataChiusura && (
              <Chip size="small" label={new Date(snapshot.dataChiusura).toLocaleString()} />
            )}
          </Stack>

          {branch ? (
            <>
              {isAffissioni && totalsAff && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {typeof totalsAff.totalOffices === "number" && <Chip label={`Uffici catalogo: ${totalsAff.totalOffices}`} />}
                    {typeof totalsAff.managed === "number" && <Chip color="info" label={`Gestiti: ${totalsAff.managed}`} />}
                    {typeof totalsAff.completed === "number" && <Chip color="success" label={`Completati: ${totalsAff.completed}`} />}
                    {typeof totalsAff.inProgress === "number" && <Chip color="warning" label={`In Progress: ${totalsAff.inProgress}`} />}
                    {typeof totalsAff.notStarted === "number" && <Chip color="error" label={`Non Avviati: ${totalsAff.notStarted}`} />}
                  </Stack>
                </>
              )}

              {isDepositi && totalsDepositi && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip color="info"    label={`Depositi: ${totalsDepositi.count}`} />
                    <Chip color="success" label={`Completati: ${totalsDepositi.completed}`} />
                    <Chip color="warning" label={`In Progress: ${totalsDepositi.inProgress}`} />
                    <Chip color="error"   label={`Non Avviati: ${totalsDepositi.notStarted}`} />
                  </Stack>
                </>
              )}

              <Box mt={3}>
                {isAffissioni ? (
                  <TableSnapshotAffissioniAdmin
                    branch={branch}
                    val={Array.isArray(branch?.dettaglioPerLuogo) ? branch.dettaglioPerLuogo : []}
                    campaignName={campaignName}
                  />
                ) : (
                  <TableSnapshotDepositiAdmin
                    val={Array.isArray(snapshot?.snapshot?.dettaglioPerLuogo) ? snapshot.snapshot.dettaglioPerLuogo : []}
                    campaignName={campaignName}
                  />
                )}
              </Box>
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>Nessun dato disponibile per questa campagna.</Alert>
          )}

          {errorSnap && <Alert severity="error" sx={{ mt: 2 }}>{String(errorSnap)}</Alert>}
        </Paper>
      )}
    </Box>
  );
}
